import { CurrencyFormat } from "../../../utils";
import useConfiguratorData from "../../hooks/useConfiguratorData";
import useAccessoriesData from "../../hooks/useAccessoriesData";
import usePaymentOptions from "../../hooks/usePaymentOptions";
import useMAFSimulationData from "../../hooks/useMAFSimulationData";
import useKintoOnePersonalData from "../../hooks/useKintoOnePersonalData";
import useUsedVehicleData from "../../hooks/useUsedVehicleData";

// Servicios que cubre la cuota mensual de KINTO One
const KINTO_INCLUDED_SERVICES = [
    "Mantenciones en Concesionario Oficial",
    "Inscripción del vehículo",
    "Impuesto verde",
    "Permiso de circulación y SOAP",
    "Seguro automotriz",
    "Servicio GPS",
    "Gastos administrativos",
    "Contratación del dispositivo TAG",
];

/**
 * Panel desplegable con el detalle de la configuración cotizada.
 * Se muestra sobre el resumen fijo al pulsar "Ver detalle de tu cotización".
 */
const QuoteDetail = ({ onClose }) => {
    const { getSelectedModel, getSelectedVersion, getSelectedPrepaidMaintenancePlan } =
        useConfiguratorData();
    const { getAddedAccessories, getIgnoreAccessories } = useAccessoriesData();
    const { isCash, isMAF, isKinto } = usePaymentOptions();
    const { getMAFData } = useMAFSimulationData();
    const { getCurrentKintoData } = useKintoOnePersonalData();
    const {
        hasUsedVehicleData,
        getUsedVehicleDetails,
        getUsedVehicleMileage,
        getUsedVehicleMinPrice,
        getUsedVehicleMaxPrice,
        getUsedVehicleEstimatedPrice,
    } = useUsedVehicleData();

    const selectedModel = getSelectedModel();
    const selectedVersion = getSelectedVersion();
    const selectedPrepaidMaintenancePlan = getSelectedPrepaidMaintenancePlan();
    const accessories = getAddedAccessories();
    const ignoreAccessories = getIgnoreAccessories();
    const mafSimulationData = getMAFData();
    const kintoOnePersonalData = getCurrentKintoData();

    const hasUsedVehicle = hasUsedVehicleData();
    const usedVehicleDetails = getUsedVehicleDetails();
    const usedVehicleMileage = getUsedVehicleMileage();
    const usedVehicleMinPrice = getUsedVehicleMinPrice();
    const usedVehicleMaxPrice = getUsedVehicleMaxPrice();
    const usedVehicleEstimatedPrice = getUsedVehicleEstimatedPrice();

    if (!selectedModel || !selectedVersion) {
        return null;
    }

    // Precio dinámico según opción de pago (igual que en FixedSummary.js)
    const calculateDynamicPrice = () => {
        let basePrice = selectedVersion.price;

        if (isKinto) {
            basePrice = selectedVersion.list_price;
        } else if (isCash) {
            basePrice = selectedVersion.list_price - (selectedVersion.brand_bonus || 0);
        }

        return basePrice;
    };

    const dynamicPrice = calculateDynamicPrice();

    // Los accesorios solo cuentan si el usuario no pidió ignorarlos
    const showAccessories = accessories.length > 0 && !ignoreAccessories;
    const accessoriesPrice = accessories.reduce(
        (total, accessory) => total + (accessory.price || 0),
        0
    );

    // Las mantenciones prepagadas no aplican en contado ni en Kinto
    const showMaintenancePlan = selectedPrepaidMaintenancePlan && !isCash && !isKinto;

    // "Suscripción KINTO One - Durante 24 meses con 15.000 km disponibles."
    const kintoPlanText = kintoOnePersonalData?.selected_months
        ? `Suscripción KINTO One - Durante ${kintoOnePersonalData.selected_months} meses con ${Number(
              kintoOnePersonalData.selected_kilometers
          ).toLocaleString("es-CL")} km disponibles.`
        : "Suscripción KINTO One";

    const kintoMonthlyQuote =
        kintoOnePersonalData?.api_response?.amortization?.[
            kintoOnePersonalData.api_response.amortization.length - 1
        ]?.totalQuota;

    return (
        <div className="quote-detail">
            <div className="quote-detail__header">
                <h3 className="quote-detail__title">Detalle de configuración</h3>
                <button
                    type="button"
                    className="quote-detail__close"
                    onClick={onClose}
                    aria-label="Cerrar detalle"
                >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path
                            d="M1 1L15 15M15 1L1 15"
                            stroke="#191919"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                        />
                    </svg>
                </button>
            </div>

            <div className="quote-detail__columns">
                <div className="quote-detail__column">
                    <p className="quote-detail__column-title">Características Generales</p>
                    <p className="quote-detail__text">
                        {selectedVersion?.main_features && selectedVersion.main_features.length > 0
                            ? selectedVersion.main_features
                                  .map((feature) => feature.description)
                                  .join(", ")
                            : "Características no disponibles"}
                    </p>
                </div>

                <div className="quote-detail__column">
                    <p className="quote-detail__column-title">
                        {selectedModel?.name} {selectedVersion?.name}
                    </p>
                    {!isKinto && (
                        <p className="quote-detail__price">
                            {CurrencyFormat(dynamicPrice)}
                            <sup>(*)</sup>
                        </p>
                    )}
                </div>

                {showAccessories && (
                    <div className="quote-detail__column">
                        <p className="quote-detail__column-title">
                            Accesorios seleccionados ({accessories.length})
                        </p>
                        <p className="quote-detail__price">
                            {CurrencyFormat(accessoriesPrice)}
                            <sup>(1)</sup>
                        </p>
                        <ul className="quote-detail__list">
                            {accessories.map((accessory, index) => (
                                <li key={index}>
                                    {accessory.name} - {CurrencyFormat(accessory.price)}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {showMaintenancePlan && (
                    <div className="quote-detail__column">
                        <p className="quote-detail__column-title">
                            Mantenciones Prepagadas (MPP) - {selectedPrepaidMaintenancePlan.name}
                        </p>
                        <p className="quote-detail__price">
                            {CurrencyFormat(selectedPrepaidMaintenancePlan.price)}
                        </p>
                    </div>
                )}

                <div className="quote-detail__column">
                    <p className="quote-detail__column-title">Opción de pago</p>
                    {isCash && <p className="quote-detail__text">Contado</p>}
                    {isMAF && (
                        <>
                            <p className="quote-detail__text">
                                {mafSimulationData?.smart_buy
                                    ? "Financiado con Plan Renueve"
                                    : "Financiado"}
                            </p>
                            {mafSimulationData?.fee_value && (
                                <p className="quote-detail__price">
                                    {mafSimulationData.number_fees} cuotas de{" "}
                                    {CurrencyFormat(Number(mafSimulationData.fee_value))}
                                </p>
                            )}
                        </>
                    )}
                    {isKinto && (
                        <>
                            <p className="quote-detail__text">{kintoPlanText}</p>
                            {kintoOnePersonalData?.selected_downpayment ? (
                                <p className="quote-detail__text">
                                    Tu pie es de {kintoOnePersonalData.selected_downpayment}%.
                                </p>
                            ) : null}
                            {kintoMonthlyQuote && (
                                <p className="quote-detail__price">
                                    Cuota mensual: {CurrencyFormat(Number(kintoMonthlyQuote))}
                                </p>
                            )}
                        </>
                    )}
                </div>

                {isKinto && (
                    <div className="quote-detail__column">
                        <p className="quote-detail__column-title">
                            Incluido en la cuota KINTO One
                        </p>
                        <p className="quote-detail__text">{KINTO_INCLUDED_SERVICES.join(" / ")}</p>
                    </div>
                )}

                {hasUsedVehicle && (
                    <div className="quote-detail__column">
                        <p className="quote-detail__column-title">Tu usado</p>
                        {usedVehicleDetails && (
                            <p className="quote-detail__text">
                                El valor de mercado de tu {usedVehicleDetails.make}{" "}
                                {usedVehicleDetails.model} {usedVehicleDetails.year}
                                {usedVehicleMileage ? ` (${usedVehicleMileage.name})` : ""} ronda
                                entre {CurrencyFormat(usedVehicleMinPrice)} y{" "}
                                {CurrencyFormat(usedVehicleMaxPrice)}.
                            </p>
                        )}
                        {usedVehicleEstimatedPrice && (
                            <p className="quote-detail__highlight">
                                ¡Si realmente está Excelente podrías obtener{" "}
                                {CurrencyFormat(usedVehicleEstimatedPrice)}!
                            </p>
                        )}
                    </div>
                )}
            </div>

            <div className="quote-detail__disclaimer">
                <p>
                    (*) Nuestro precio sugerido incluye bono marca y condiciones de financiación.
                    Para más detalle ver términos y condiciones comerciales.
                </p>
                <p>
                    (1) Precios sugeridos. El valor por la instalación no está incluido en el
                    precio publicado y varia por concesionario.
                </p>
                <p>(2) Plan de mantenciones prepagadas sujeto a términos y condiciones.</p>
                <p>
                    (3) El valor del Usado se calcula en base a los datos suministrados por
                    Infopress y son meramente referenciales, el valor definitivo será definido por
                    el Dealer seleccionado.
                </p>
            </div>
        </div>
    );
};

export default QuoteDetail;
